import moment from 'moment';
import { solar2lunar } from 'solarlunar';

// 定义阴历日期接口
interface LunarDate {
  year: string;
  month: string;
  day: string;
}

// 定义日历日期接口
export interface CalendarDay {
  date: string; // 格式: YYYY-MM-DD
  weekday: number; // 0-6, 0表示周日
  lunar: LunarDate; // 阴历年月日
}

/**
 * 生成包含一个月的42天（6周）的二维数组
 * @param monthDate 月份日期，格式: YYYY-MM-DD
 * @returns 二维数组，每个元素包含当前日期、周几和准确的阴历年月日
 */
export function generateCalendarDays(monthDate: string): CalendarDay[][] {
  // 获取传入月份的第一天
  const firstDayOfMonth = moment(monthDate).startOf('month');

  // 获取第一天是周几（0-6，0表示周日）
  const firstDayWeekday = firstDayOfMonth.day();

  // 计算日历开始日期（可能是上个月的日期）
  const calendarStartDate = firstDayOfMonth
    .clone()
    .subtract(firstDayWeekday, 'days');

  // 创建42天的数组（6周 × 7天）
  const calendarDays: CalendarDay[] = [];

  for (let i = 0; i < 42; i++) {
    const currentDate = calendarStartDate.clone().add(i, 'days');
    const dateStr = currentDate.format('YYYY-MM-DD');
    const weekday = currentDate.day();

    // 使用solarlunar库生成准确的阴历日期
    const lunar = generateLunarDate(currentDate);

    calendarDays.push({
      date: dateStr,
      weekday,
      lunar,
    });
  }

  // 将一维数组转换为二维数组（6周 × 7天）
  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < 6; i++) {
    weeks.push(calendarDays.slice(i * 7, (i + 1) * 7));
  }

  return weeks;
}

/**
 * 使用solarlunar库生成准确的阴历日期
 * @param date 公历日期
 * @returns 阴历日期对象
 */
function generateLunarDate(date: moment.Moment): LunarDate {
  // 获取年月日
  const year = date.year();
  const month = date.month() + 1; // moment的月份从0开始
  const day = date.date();

  // 使用solarlunar库将公历日期转换为阴历日期
  const lunarInfo = solar2lunar(year, month, day);

  return {
    year: lunarInfo.yearCn,
    month: lunarInfo.monthCn,
    day: lunarInfo.dayCn,
  };
}

// 英文星期表示
export const WEEKDAYS = {
  // 完整形式
  full: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  // 缩写形式
  short: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  // 中文
  cn: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
};
